import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { FieldIcon } from '@modules/common/components/FieldIcon';
import { TaxonomyNodeSuggestionOption } from '@modules/common/components/TaxonomyNodesAutoComplete/types';
import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import {
  ChevronRightRounded,
  KeyboardReturnRounded,
} from '@mui/icons-material';
import { Box, BoxProps, Stack, Typography, alpha } from '@mui/material';
import { LabelType } from 'generated/graphql';

interface TaxonomyNodeSuggestionProps {
  option: TaxonomyNodeSuggestionOption;
  optionProps: BoxProps;
  shouldShowKeyboardIcon?: boolean;
}

export function TaxonomyNodeSuggestion({
  option,
  optionProps,
  shouldShowKeyboardIcon = true,
}: TaxonomyNodeSuggestionProps) {
  const isMoreOption = option.id.endsWith('_more');

  const getLabel = () => {
    if (isMoreOption) {
      return (
        <Box component="span" sx={{ fontWeight: 400 }}>
          More...
        </Box>
      );
    }

    if (option.displayName) {
      return option.displayName;
    }

    return null;
  };

  const getFieldKey = () => {
    if (option.groupType === LabelType.L1) {
      return 'l1';
    }

    if (option.groupType === LabelType.Aspect) {
      return 'entity';
    }

    return 'reason';
  };

  return (
    <Box
      {...optionProps}
      display="flex"
      onMouseDown={isMoreOption ? (event) => event.preventDefault() : undefined}
      sx={{
        ...optionProps.sx,
        flexDirection: 'row',
        justifyContent: 'space-between',
        '&.Mui-focused': {
          '.KeyboardIconContainer': {
            opacity: 1,
          },
          '.EditIconContainer': {
            opacity: 1,
          },
        },
        pr: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ maxWidth: 'calc(100% - 40px)', minWidth: 0 }}
      >
        {isMoreOption ? (
          <ChevronRightRounded fontSize="small" color="primary" />
        ) : (
          <FieldIcon field={getFieldKey()} />
        )}
        {option.category && (
          <Stack>
            <CategoryIcon
              category={mapEdgeCategoryToCategory(option.category)}
              size={16}
            />
          </Stack>
        )}
        <Typography
          variant="subtitle2"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getLabel()}
        </Typography>
      </Stack>
      {shouldShowKeyboardIcon && (
        <Box
          component="span"
          bgcolor={(theme) => alpha(theme.palette.primary.main, 0.16)}
          className="KeyboardIconContainer"
          px={1}
          borderRadius={1}
          sx={{ opacity: 0 }}
        >
          <KeyboardReturnRounded
            fontSize="small"
            sx={{ color: (theme) => theme.palette.primary.main }}
          />
        </Box>
      )}
    </Box>
  );
}
